<template>
  <!-- Exportar y sus filtros a la izquierda, como en Comunidades. Sin «Añadir
       nuevo»: los CUPS se dan de alta desde la ficha de la comunidad o del
       inmueble (ADSREC-0029, ajustes de ADS del 23/10). -->
  <div class="flex pb-6">
    <div class="flex w-full md:w-auto justify-between gap-0 md:gap-4">
      <ExportFetch
        exportEndpoint="/supply/user/export"
        :fileName="$t(`${i18nRoot}.fileName`)"
        :exportType="type"
        :extraParams="exportParams"
        :date-range="internalUser && !route.query.id"
        :from-label="$t('admin.export.dateFrom')"
        :to-label="$t('admin.export.dateTo')"
        :selection="internalUser ? selection : null"
        @export-started="handleExportStarted"
        @export-completed="handleExportCompleted"
        @export-failed="handleExportFailed"
      />

      <PrintTableComponent
        :title="title"
        :fields="translatedFields"
        :items="items"
        @print-started="() => console.log('Impresión iniciada')"
        @print-completed="() => console.log('Impresión completada')"
        @print-failed="(error) => console.error('Error al imprimir:', error)"
      />
    </div>
  </div>

  <TitlePrimary :title="title" class="w-full" />

  <div class="md:hidden">
    <CardTable :fields="translatedFields" :items="items.length > 0 ? [items[currentItemIndex]] : []"
      :showOptions="false" :route="`/${detailRoute}`" />
    <MobilePaginateComponent v-if="items.length > 0" :totalPages="totalPages" :modelValue="currentPage"
      :totalItems="totalItems" :currentItemIndex="currentItemIndex" :itemsPerPage="10"
      @update:modelValue="handlePageChange" @update:currentItemIndex="handleItemChange" />
  </div>

  <div class="hidden md:block">
    <TableComponent
      :filter-definitions="filterDefinitions" :filter-error="filterError"
      v-model:filters="filters"
      @filter-change="onFilterChange"
      :fields="translatedFields"
      :items="items"
      :showOptions="true"
      :route="detailRoute"
      :listOptions="translatedOptionsList"
      :selection="internalUser ? selection : null"
    />

    <PaginateComponent
      :totalPages="totalPages" :paginationInfo="paginationInfo"
      :modelValue="currentPage"
      @update:modelValue="handlePageChange"
    />
  </div>
</template>

<script setup>
/**
 * Puntos de suministro (CUPS) de luz o gas dentro de Comunidades e Inmuebles.
 *
 * Es la tabla que los clientes veían en los apartados Luz y Gas antes de que
 * esos apartados pasaran a listar solicitudes. Ahora vive como pestaña dentro
 * de Comunidades para clientes y ADS. Conserva los mismos endpoints y permisos
 * de los apartados Luz/Gas; una cuenta seleccionada mantiene su filtro `id`.
 */
import { ref, computed, reactive, watch, onMounted } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useExportStore } from '@/stores/exportStore';
import { useTableFilters } from '@/composables/useTableFilters';
import { useRowSelection } from '@/composables/useRowSelection';
import { usePaginationInfo } from '@/composables/usePaginationInfo';
import { normalizeSupplyRows } from '@/services/supplyRows';
import { supplyRequestOptions } from '@/services/supplyRequestOptions';
import { hasAnyRole, isAdsUser } from '@/services/userRoles';
import { useRoute } from 'vue-router';

import TableComponent from '@/components/table/TableComponent.vue';
import PaginateComponent from '@/components/paginate/PaginateComponent.vue';
import ExportFetch from '@/components/fetch/exportFetch.vue';
import PrintTableComponent from '@/components/print/PrintTableComponent.vue';
import TitlePrimary from '@/components/title/title-primary.vue';
import CardTable from '@/components/table/CardTable.vue';
import MobilePaginateComponent from '@/components/paginate/MobilePaginateComponent.vue';

const props = defineProps({
  type: { type: String, required: true, validator: (value) => ['light', 'gas'].includes(value) },
  title: { type: String, default: '' },
  // Apartado desde el que se abre (ADS-0123): Inmuebles lista los CUPS de los
  // inmuebles y Comunidades los de las comunidades. Vacío: todos los de la cuenta.
  propertyType: { type: String, default: '' },
});
const internalUser = isAdsUser();

const { t } = useI18n();
const exportStore = useExportStore();
const route = useRoute();
const endpointScope = computed(() => !route.query.id && hasAnyRole(['superadmin']) ? 'superadmin' : 'user');
const accountParams = () => ({
  ...(route.query.id ? { id: route.query.id } : {}),
  ...(props.propertyType ? { property_type: props.propertyType } : {}),
});

const i18nRoot = computed(() => (props.type === 'gas' ? 'supplyGas' : 'supplyLight'));
const detailRoute = computed(() => (props.type === 'gas' ? 'gas-detail' : 'light-detail'));
const title = computed(() => props.title || t(`${i18nRoot.value}.title`));

// Comunidades e Inmuebles comparten el resumen del CUPS y sus filtros:
// titular, documento, ubicación y opciones, en el mismo orden. ADS ve la
// cartera de muchas cuentas: la primera columna dice de cuál es cada CUPS,
// con el mismo nombre que en la pestaña de Comunidades (ADS-0186).
const translatedFields = computed(() => [
  ...(internalUser ? [{ key: 'account', label: t('property.communityForm.fields.account') }] : []),
  { key: 'cups', label: t(`${i18nRoot.value}.table.cups`) },
  { key: 'holder_name', label: t(`${i18nRoot.value}.table.holderName`) },
  { key: 'holder_document', label: t(`${i18nRoot.value}.table.holderDocument`) },
  { key: 'address', label: t(`${i18nRoot.value}.table.address`) },
  // Resumen del historial de suministros: CP, población y provincia de la finca.
  { key: 'postal_code', label: t(`${i18nRoot.value}.table.postalCode`) },
  { key: 'municipality', label: t(`${i18nRoot.value}.table.municipality`) },
  { key: 'province', label: t(`${i18nRoot.value}.table.province`) },
  { key: 'opciones', label: t(`${i18nRoot.value}.table.options`) },
]);

const filterDefinitions = computed(() => translatedFields.value
  .filter((field) => field.key !== 'opciones')
  .map((field) => ({ key: field.key, type: 'text' })));

// "Crear solicitud" desde el CUPS (ADS-0125): el formulario recibe el suministro de origen.
const translatedOptionsList = computed(() => supplyRequestOptions(t));

// --- Estado ---
const items = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const currentItemIndex = ref(0);
const { paginationInfo, setPaginationInfo } = usePaginationInfo();

const { filters, activeFilters, isFiltering, filterError, runSearch, onFilterChange, clearFilters } = useTableFilters({
  endpoint: () => `/supply/${endpointScope.value}/search`,
  perPage: 10,
  translate: t,
  // `portfolio`: filtrar sobre la misma cartera que lista la pestaña; sin él, la
  // búsqueda de ADS es global para el autocompletado de los formularios (ADSREC-0006).
  extraParams: () => ({ type: props.type, portfolio: 1, ...accountParams() }),
  onResults: (data) => {
    const page = data?.value;
    if (!page) return;
    items.value = normalizeSupplyRows(page.data || [], props.propertyType);
    totalPages.value = page.last_page || 1;
    setPaginationInfo(page);
    totalItems.value = page.total ?? 0;
    currentPage.value = page.current_page || 1;
    currentItemIndex.value = 0;
  },
  onCleared: () => {
    currentPage.value = 1;
    currentItemIndex.value = 0;
    fetchDataFromApi();
  },
});

// ADS elige qué CUPS exportar con casillas: filas sueltas o todo lo filtrado
// (ADSREC-0029). «Todo» se suelta al cambiar los filtros.
const selection = reactive(useRowSelection(totalItems));
watch(activeFilters, () => selection.releaseAll());

// La exportación baja lo que muestra la pestaña: la cuenta abierta, el tipo
// de finca y los filtros de cabecera (ADS-0186). ADS, fuera de la ficha de
// una cuenta, exporta además por fecha de alta, un año como máximo.
const exportParams = computed(() => ({
  ...accountParams(),
  ...Object.fromEntries(Object.entries(activeFilters.value).map(([field, value]) => [`filters[${field}]`, value])),
}));

const fetchDataFromApi = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/supply/${endpointScope.value}/list/${props.type}`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params: { per_page: 10, page: currentPage.value, ...accountParams() },
      }
    );
    const page = response.data?.value;
    items.value = normalizeSupplyRows(page?.data || [], props.propertyType);
    totalPages.value = page?.last_page || 1;
    setPaginationInfo(page || {});
    totalItems.value = page?.total ?? 0;
  } catch (error) {
    items.value = [];
    if (!error.response || error.response.status !== 404) {
      console.error('Error fetching supplies:', error);
    }
  }
};

const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  currentItemIndex.value = 0;
  if (isFiltering.value) {
    await runSearch(newPage);
  } else {
    await fetchDataFromApi();
  }
};

const handleItemChange = (newIndex) => {
  if (newIndex >= 0 && newIndex < items.value.length) {
    currentItemIndex.value = newIndex;
  }
};

const handleExportStarted = () => exportStore.startExport(t(`${i18nRoot.value}.title`));
const handleExportCompleted = () => exportStore.completeExport();
const handleExportFailed = () => exportStore.failExport(t('search.failed'));

// Cambiar de pestaña Luz ↔ Gas reutiliza este componente: se vuelve a la
// primera página sin arrastrar los filtros del otro tipo.
watch(() => [props.type, props.propertyType, route.query.id], () => {
  // Otra pestaña u otra cuenta: empieza sin nada marcado (ADSREC-0029).
  selection.clear();
  currentPage.value = 1;
  currentItemIndex.value = 0;
  if (isFiltering.value) {
    clearFilters();
  } else {
    fetchDataFromApi();
  }
});

onMounted(fetchDataFromApi);
</script>
